import {
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subHours,
  subMonths,
} from "date-fns";

import { getClientCRMFlag } from "./clientCustomCRMFlags";

/**
 * Narrower than `Filters/types`' `DurationFilter` (whose `value`/`displayValue` widen to
 * `string | string[]` for the multi-select filters) — a duration is always a single preset, and
 * the PMS slice declares it that way. Assignable to `DurationFilter` either way.
 */
export interface DefaultDuration {
  value: string;
  displayValue: string;
  startDate: string;
  endDate: string;
}

/**
 * Single source of truth for the duration ("date filter") presets.
 *
 * Every screen's default used to be re-derived — and the "last-30-days" literal re-typed — in the
 * slices, in Filters.tsx's clear-chip checks and in each of the three pickers, which is how they
 * drifted apart. They all read this module instead now.
 *
 * Deliberately JSX-free so the redux slices can import it; the option LIST (which carries icons)
 * stays in `components/Filters/utils/constants.tsx`.
 */

export const LAST_6_MONTHS = "last-6-months";
export const LAST_60_DAYS = "last-60-days";
export const LAST_30_DAYS = "last-30-days";

const DURATION_LABELS: Record<string, string> = {
  today: "Today",
  "last-24-hours": "Last 24 Hours",
  "last-7-days": "Last 7 Days",
  [LAST_30_DAYS]: "Last 30 Days",
  [LAST_60_DAYS]: "Last 60 Days",
  [LAST_6_MONTHS]: "Last 6 Months",
  "last-month": "Last Month",
  "this-month": "This Month",
  custom: "Custom",
};

export const getDurationDisplayValue = (value: string): string =>
  DURATION_LABELS[value] ?? "";

/** Whether this tenant sees "Last 6 Months" in the picker at all. */
export const showsLast6MonthsOption = (): boolean =>
  getClientCRMFlag("hasLast6MonthsFilter");

/**
 * The preset a tenant's date filters boot with, on every screen.
 * 6 months wins over 60 days if a tenant is ever given both flags.
 */
export const getDefaultDurationValue = (): string => {
  if (getClientCRMFlag("hasLast6MonthsFilter")) return LAST_6_MONTHS;
  if (getClientCRMFlag("hasLast60DaysDefault")) return LAST_60_DAYS;
  return LAST_30_DAYS;
};

/**
 * Date range for a preset. `now` is injected so the pickers and the slices agree, and so tests
 * don't have to freeze the clock to assert.
 */
export const getDurationRange = (
  value: string,
  now: Date
): { from: Date | null; to: Date | null } => {
  switch (value) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "last-24-hours":
      return { from: subHours(now, 24), to: now };
    case "last-7-days":
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    case LAST_30_DAYS:
      return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
    case LAST_60_DAYS:
      return { from: startOfDay(subDays(now, 60)), to: endOfDay(now) };
    case LAST_6_MONTHS:
      return { from: startOfDay(subMonths(now, 6)), to: endOfDay(now) };
    case "last-month": {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case "this-month":
      return { from: startOfMonth(now), to: endOfDay(now) };
    default:
      // "all", "custom" and anything unknown: the caller keeps whatever dates it already has.
      return { from: null, to: null };
  }
};

/** The full default duration object the slices seed their initial state with. */
export const getDefaultDuration = (): DefaultDuration => {
  const value = getDefaultDurationValue();
  const { from, to } = getDurationRange(value, new Date());

  return {
    value,
    displayValue: getDurationDisplayValue(value),
    startDate: from?.toISOString() ?? "",
    endDate: to?.toISOString() ?? "",
  };
};

/**
 * Custom ranges are capped at six months.
 *
 * Anchored at the TO date on purpose: `subMonths` clamps short months (Aug 31 -> Feb 28), so
 * measuring forward from FROM with `setMonth(+6)` landed on Aug 28 and rejected the last-6-months
 * preset outright on the seven clamp days each year. Measuring back from TO mirrors how the preset
 * is generated, so the boundary is exact on every calendar day.
 */
export const exceedsSixMonths = (fromDate: Date, toDate: Date): boolean =>
  startOfDay(fromDate) < subMonths(startOfDay(toDate), 6);
